import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowRight,
  FaBookOpen,
  FaClinicMedical,
  FaMotorcycle,
  FaSearch,
  FaStore,
  FaTags,
  FaTimes,
  FaTruck,
  FaUtensils,
} from "react-icons/fa";
import PlatformOverviewWiki from "./PlatformOverviewWiki.js";
import RestaurantPlatformWiki from "./RestaurantPlatformWiki.js";
import RetailWholesaleWiki from "./RetailWholesaleWiki.js";
import HealthcarePlatformWiki from "./HealthcarePlatformWiki.js";
import BrandPlatformWiki from "./BrandPlatformWiki.js";
import ThreePLPlatformWiki from "./ThreePLPlatformWiki.js";
import RiderPlatformWiki from "./RiderPlatformWiki.js";
import "./KnowledgeBaseWiki.css";

const TABS = [
  {
    key: "overview",
    label: "Platform overview",
    shortLabel: "Overview",
    description: "Actors, orders, money, audit trails, and platform surfaces.",
    keywords:
      "platform apps lifecycle orders money wallet audit entitlements sidebar customer vendor alerts alert settings notifications email sms whatsapp",
    accent: "#1a2b4a",
    icon: FaBookOpen,
    component: PlatformOverviewWiki,
  },
  {
    key: "restaurants",
    label: "Restaurants",
    shortLabel: "Restaurants",
    description: "Menus, opening hours, food orders, delivery, and settlement.",
    keywords:
      "restaurant food menu kitchen hours outlet options addons cod delivery fees",
    accent: "#2475a8",
    icon: FaUtensils,
    component: RestaurantPlatformWiki,
  },
  {
    key: "retail-wholesale",
    label: "Retail & Wholesale",
    shortLabel: "Retail & wholesale",
    description: "Catalogues, inventory, B2B pricing, fulfilment, and COD.",
    keywords:
      "retail wholesale stock inventory catalogue catalog b2b quote order warehouse",
    accent: "#c75b22",
    icon: FaStore,
    component: RetailWholesaleWiki,
  },
  {
    key: "healthcare",
    label: "Healthcare",
    shortLabel: "Healthcare",
    description: "Prescriptions, restricted products, services, and providers.",
    keywords:
      "healthcare pharmacy clinic lab prescription rx controlled age service appointment",
    accent: "#b83e68",
    icon: FaClinicMedical,
    component: HealthcarePlatformWiki,
  },
  {
    key: "brands",
    label: "Brands",
    shortLabel: "Brands",
    description: "Brand ownership, storefronts, outlets, media, and orders.",
    keywords:
      "brand owner storefront featured images outlets media products orders quotes",
    accent: "#79539a",
    icon: FaTags,
    component: BrandPlatformWiki,
  },
  {
    key: "3pl",
    label: "3PL Partners",
    shortLabel: "3PL partners",
    description: "Partner operations, riders, service areas, and dispatch.",
    keywords:
      "3pl logistics partner riders carrier service area dispatch delivery fleet",
    accent: "#397b4a",
    icon: FaTruck,
    component: ThreePLPlatformWiki,
  },
  {
    key: "riders",
    label: "Riders",
    shortLabel: "Riders",
    description: "Onboarding, assignments, delivery flow, COD, and performance.",
    keywords:
      "rider courier assignment delivery cod carrier online offline performance",
    accent: "#596b80",
    icon: FaMotorcycle,
    component: RiderPlatformWiki,
  },
];

const getHashTab = () => {
  if (typeof window === "undefined") return TABS[0].key;
  const hash = (window.location.hash || "").replace("#", "");
  return TABS.some((tab) => tab.key === hash) ? hash : TABS[0].key;
};

const KnowledgeBaseWiki = () => {
  const [active, setActive] = useState(getHashTab);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const syncHash = () => setActive(getHashTab());
    window.addEventListener("hashchange", syncHash);
    return () => window.removeEventListener("hashchange", syncHash);
  }, []);

  const select = (key) => {
    setActive(key);
    setQuery("");
    try {
      window.history.replaceState(null, "", `#${key}`);
    } catch (error) {
      window.location.hash = key;
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const activeTab = TABS.find((tab) => tab.key === active) || TABS[0];
  const ActiveComponent = activeTab.component;
  const ActiveIcon = activeTab.icon;

  const searchResults = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return TABS.filter((tab) =>
      `${tab.label} ${tab.description} ${tab.keywords}`
        .toLowerCase()
        .includes(term)
    );
  }, [query]);

  return (
    <div
      className="content kb-workspace"
      style={{ "--kb-accent": activeTab.accent }}
    >
      <header className="kb-header">
        <div className="kb-header__title">
          <span className="kb-header__eyebrow">Operations handbook</span>
          <h1>
            <span>
              <FaBookOpen aria-hidden="true" />
            </span>
            Lusuku knowledge base
          </h1>
          <p>
            Practical guidance for platform operations, vendors, logistics,
            and fulfilment teams.
          </p>
        </div>

        <label className="kb-search">
          <FaSearch aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search guides and topics"
            aria-label="Search knowledge base guides"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear knowledge base search"
              title="Clear search"
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </label>
      </header>

      {query && (
        <section className="kb-search-results" aria-label="Search results">
          <div className="kb-search-results__heading">
            <span>Guide matches</span>
            <strong>
              {searchResults.length}{" "}
              {searchResults.length === 1 ? "result" : "results"}
            </strong>
          </div>
          {searchResults.length ? (
            <div className="kb-search-results__list">
              {searchResults.map((tab) => {
                const ResultIcon = tab.icon;
                return (
                  <button
                    type="button"
                    key={tab.key}
                    onClick={() => select(tab.key)}
                  >
                    <span style={{ "--result-accent": tab.accent }}>
                      <ResultIcon aria-hidden="true" />
                    </span>
                    <span>
                      <strong>{tab.label}</strong>
                      <small>{tab.description}</small>
                    </span>
                    <FaArrowRight aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="kb-search-results__empty">
              <FaSearch aria-hidden="true" />
              <span>
                No guide matches “{query}”. Try a vendor type, order, wallet,
                delivery, or audit.
              </span>
            </div>
          )}
        </section>
      )}

      <nav className="kb-guide-nav" aria-label="Knowledge base guides">
        <div className="kb-guide-nav__label">
          <span>Guide directory</span>
          <strong>{TABS.length} operational guides</strong>
        </div>
        <div className="kb-guide-nav__tabs" role="tablist">
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={isActive ? "is-active" : ""}
                style={{ "--tab-accent": tab.accent }}
                onClick={() => select(tab.key)}
              >
                <span>
                  <TabIcon aria-hidden="true" />
                </span>
                {tab.shortLabel}
              </button>
            );
          })}
        </div>
      </nav>

      <section className="kb-current-guide">
        <div className="kb-current-guide__heading">
          <span className="kb-current-guide__icon">
            <ActiveIcon aria-hidden="true" />
          </span>
          <div>
            <span>Current guide</span>
            <h2>{activeTab.label}</h2>
            <p>{activeTab.description}</p>
          </div>
        </div>
      </section>

      <section
        className="kb-policy-note"
        aria-label="Business hours policy for every vendor type"
      >
        <span>Business hours, every vendor type</span>
        <p>
          Shops, wholesalers, brands and pharmacies accept orders 24/7 until
          they save Business Hours on their profile; customers see an
          &ldquo;Open 24/7&rdquo; badge. Retail and wholesale orders carry a
          two day delivery window, so an overnight order simply waits for the
          vendor&rsquo;s next login. Saving Business Hours limits ordering to
          those times and shows the next opening while closed; the 24/7 preset
          restores round-the-clock ordering. Restaurants should always set
          hours: without them the storefront shows no open badge, and a food
          order placed against unpublished hours is a promise nobody made.
          Order placement re-checks hours on the server in every case.
        </p>
      </section>

      <main
        className="kb-content"
        role="tabpanel"
        aria-label={`${activeTab.label} guide`}
      >
        <ActiveComponent />
      </main>
    </div>
  );
};

export default KnowledgeBaseWiki;
